import { useQueryClient } from '@tanstack/react-query'
import { useLeagueDetail, useLeagueGameweekStatus } from '../queries'
import {
  LeagueFormat,
  LeagueScoringSystem,
  LeagueTransferOption,
  TransferWindowState,
  FixtureStatus,
  type League,
  type LeagueSummary,
  type StandingsEntry,
  type LeagueMember,
  type H2HFixture,
  type LeagueEvent,
} from '../types'
import { leagueKeys } from '../queries'

// ─── Mock data ────────────────────────────────────────────────────────────────
// Replace with real TanStack Query hooks when POST /v1/leagues is ready.
// The mock is an H2H league with 7 members (even → ghost "Média" entry) and
// GW3 active with the transfer window open for ~2h 34m.

const MOCK_LEAGUE: League = {
  id: 'mock-league-1',
  name: 'Os Incompetentes FC',
  format: LeagueFormat.HeadToHead,
  transferOption: LeagueTransferOption.OptionB,
  scoringSystem: LeagueScoringSystem.NossaPreset,
  joinCode: 'OSI-4729',
  memberCount: 7,
  maxParticipants: 100,
  isAdmin: true,
  isLocked: false,
  gwStatus: {
    currentGameweek: 3,
    windowState: TransferWindowState.Open,
    windowChangesAt: new Date(Date.now() + 2 * 60 * 60 * 1000 + 34 * 60 * 1000).toISOString(),
    scoresFinalized: true,
  },
}

const MOCK_STANDINGS: StandingsEntry[] = [
  {
    rank: 1, previousRank: 2,
    userId: 'u1', squadId: 'sq1', teamName: 'Lendas do Futebol',
    gwPoints: 78, totalPoints: 234, transfersUsed: 3,
    isCurrentUser: false, isAverageParticipant: false, hasSquad: true,
    h2hPoints: 9, wins: 3, draws: 0, losses: 0,
  },
  {
    rank: 2, previousRank: 1,
    userId: 'u2', squadId: 'sq2', teamName: 'Os Incompetentes FC',
    gwPoints: 65, totalPoints: 198, transfersUsed: 5,
    isCurrentUser: true, isAverageParticipant: false, hasSquad: true,
    h2hPoints: 6, wins: 2, draws: 0, losses: 1,
  },
  {
    rank: 3, previousRank: 3,
    userId: 'u3', squadId: 'sq3', teamName: 'Bola Murcha United',
    gwPoints: 51, totalPoints: 187, transfersUsed: 2,
    isCurrentUser: false, isAverageParticipant: false, hasSquad: true,
    h2hPoints: 6, wins: 2, draws: 0, losses: 1,
  },
  {
    rank: 4, previousRank: 6,
    userId: 'u4', squadId: 'sq4', teamName: 'Goleada Garantida',
    gwPoints: 48, totalPoints: 176, transfersUsed: 4,
    isCurrentUser: false, isAverageParticipant: false, hasSquad: true,
    h2hPoints: 3, wins: 1, draws: 0, losses: 2,
  },
  {
    rank: 5, previousRank: 4,
    userId: '00000000-0000-0000-0000-000000000000', squadId: '', teamName: 'Média',
    gwPoints: 52, totalPoints: 163, transfersUsed: 0,
    isCurrentUser: false, isAverageParticipant: true, hasSquad: true,
    h2hPoints: 3, wins: 1, draws: 0, losses: 2,
  },
  {
    rank: 6, previousRank: 5,
    userId: 'u5', squadId: 'sq5', teamName: 'Sem Escalação FC',
    gwPoints: 0, totalPoints: 145, transfersUsed: 7,
    isCurrentUser: false, isAverageParticipant: false, hasSquad: false,
    h2hPoints: 3, wins: 1, draws: 0, losses: 2,
  },
  {
    rank: 7, previousRank: 7,
    userId: 'u6', squadId: 'sq6', teamName: 'Os Melhores do Mundo',
    gwPoints: 38, totalPoints: 132, transfersUsed: 8,
    isCurrentUser: false, isAverageParticipant: false, hasSquad: true,
    h2hPoints: 0, wins: 0, draws: 0, losses: 3,
  },
]

const MOCK_MEMBERS: LeagueMember[] = [
  { userId: 'u2', teamName: 'Os Incompetentes FC', isAdmin: true,  isCurrentUser: true,  joinedAt: '2026-03-01T10:00:00Z' },
  { userId: 'u1', teamName: 'Lendas do Futebol',   isAdmin: false, isCurrentUser: false, joinedAt: '2026-03-01T10:05:00Z' },
  { userId: 'u3', teamName: 'Bola Murcha United',  isAdmin: false, isCurrentUser: false, joinedAt: '2026-03-02T14:30:00Z' },
  { userId: 'u4', teamName: 'Goleada Garantida',   isAdmin: false, isCurrentUser: false, joinedAt: '2026-03-03T09:15:00Z' },
  { userId: 'u5', teamName: null,                  isAdmin: false, isCurrentUser: false, joinedAt: '2026-03-04T18:00:00Z' },
  { userId: 'u6', teamName: 'Os Melhores do Mundo',isAdmin: false, isCurrentUser: false, joinedAt: '2026-03-05T11:20:00Z' },
  { userId: 'u7', teamName: 'Sem Escalação FC',    isAdmin: false, isCurrentUser: false, joinedAt: '2026-03-06T08:45:00Z' },
]

const MOCK_FIXTURES: H2HFixture[] = [
  // GW1 — finished
  { gameweek: 1, homeUserId: 'u2', homeTeamName: 'Os Incompetentes FC', homePoints: 72,   awayUserId: 'u1', awayTeamName: 'Lendas do Futebol',   awayPoints: 89,   status: FixtureStatus.Finished, isCurrentUserMatch: true  },
  { gameweek: 1, homeUserId: 'u3', homeTeamName: 'Bola Murcha United',  homePoints: 61,   awayUserId: 'u4', awayTeamName: 'Goleada Garantida',   awayPoints: 44,   status: FixtureStatus.Finished, isCurrentUserMatch: false },
  { gameweek: 1, homeUserId: 'u5', homeTeamName: 'Sem Escalação FC',    homePoints: 0,    awayUserId: 'u6', awayTeamName: 'Os Melhores do Mundo', awayPoints: 55,   status: FixtureStatus.Finished, isCurrentUserMatch: false },
  { gameweek: 1, homeUserId: 'u7', homeTeamName: null as unknown as string, homePoints: 58, awayUserId: 'media', awayTeamName: 'Média', awayPoints: 62, status: FixtureStatus.Finished, isCurrentUserMatch: false },
  // GW2 — finished
  { gameweek: 2, homeUserId: 'u2', homeTeamName: 'Os Incompetentes FC', homePoints: 61,   awayUserId: 'u3', awayTeamName: 'Bola Murcha United',  awayPoints: 75,   status: FixtureStatus.Finished, isCurrentUserMatch: true  },
  { gameweek: 2, homeUserId: 'u1', homeTeamName: 'Lendas do Futebol',   homePoints: 67,   awayUserId: 'u4', awayTeamName: 'Goleada Garantida',   awayPoints: 53,   status: FixtureStatus.Finished, isCurrentUserMatch: false },
  // GW3 — current (upcoming — window open)
  { gameweek: 3, homeUserId: 'u2', homeTeamName: 'Os Incompetentes FC', homePoints: null, awayUserId: 'u4', awayTeamName: 'Goleada Garantida',   awayPoints: null, status: FixtureStatus.Upcoming, isCurrentUserMatch: true  },
  { gameweek: 3, homeUserId: 'u1', homeTeamName: 'Lendas do Futebol',   homePoints: null, awayUserId: 'u3', awayTeamName: 'Bola Murcha United',  awayPoints: null, status: FixtureStatus.Upcoming, isCurrentUserMatch: false },
]

const MOCK_EVENTS: LeagueEvent[] = []

// ─── Hook ─────────────────────────────────────────────────────────────────────

export type LeagueScreenData = {
  league: League
  standings: StandingsEntry[]
  members: LeagueMember[]
  fixtures: H2HFixture[]
  events: LeagueEvent[]
  isLoading: boolean
}

export function useLeagueScreen(leagueId: string): LeagueScreenData {
  const queryClient = useQueryClient()

  // Check leagues list cache (populated when user views dashboard/ligas page)
  const leaguesList = queryClient.getQueryData<LeagueSummary[]>(leagueKeys.list())
  const leagueFromList = leaguesList?.find(l => l.id === leagueId)

  // Fallback: creation result cache (set by useLeagueCreation onSuccess)
  const cachedCreation = queryClient.getQueryData<{ name?: string }>(leagueKeys.detail(leagueId))

  // Fetch league detail for fields not in the list cache (e.g. joinCode)
  const { data: leagueDetail } = useLeagueDetail(leagueId)

  // Fetch live gameweek + transfer window status from the API
  const { data: gwStatusData } = useLeagueGameweekStatus(leagueId)

  // Use full mock for the known demo league; return a fresh state for real leagues
  const isKnownMock = leagueId === MOCK_LEAGUE.id
  const resolvedName = leagueFromList?.name ?? cachedCreation?.name

  // Map API response to GameweekStatus.
  // currentGameweekNumber === 0 means no GW has started yet (pre-competition).
  const currentGwForDisplay = gwStatusData && gwStatusData.currentGameweekNumber > 0
    ? gwStatusData.currentGameweekNumber
    : null
  const currentGwSummary = gwStatusData?.gameweeks.find(
    g => g.number === gwStatusData.currentGameweekNumber,
  )

  const league: League = isKnownMock
    ? MOCK_LEAGUE
    : {
        ...MOCK_LEAGUE,
        id: leagueId,
        name: resolvedName ?? 'Nova Liga',
        joinCode: leagueDetail?.joinCode ?? leagueFromList?.joinCode ?? '',
        memberCount: leagueDetail?.memberCount ?? leagueFromList?.memberCount ?? 1,
        format: (leagueDetail?.format ?? leagueFromList?.format ?? LeagueFormat.OverallPoints) as LeagueFormat,
        transferOption: (leagueDetail?.transferOption ?? LeagueTransferOption.OptionB) as LeagueTransferOption,
        isLocked: leagueDetail?.isLocked ?? false,
        gwStatus: gwStatusData
          ? {
              currentGameweek: currentGwForDisplay,
              windowState:     gwStatusData.transferWindowState,
              windowChangesAt: gwStatusData.windowChangesAt,
              scoresFinalized: currentGwSummary?.scoresFinalized ?? false,
            }
          : {
              // Still loading — treat as pre-competition to avoid flash of wrong state
              currentGameweek: null,
              windowState:     TransferWindowState.Open,
              windowChangesAt: null,
              scoresFinalized: false,
            },
      }

  return {
    league,
    standings:  isKnownMock ? MOCK_STANDINGS : [],
    members:    isKnownMock ? MOCK_MEMBERS   : [],
    // Fixtures for real leagues are fetched by useLeagueFixtures in LeagueScreen;
    // mock fixtures are only returned for the demo league.
    fixtures:   isKnownMock ? MOCK_FIXTURES  : [],
    events:     MOCK_EVENTS,
    isLoading:  false,
  }
}
